import React from "react";
import Select from "react-select";
import infoIcon from '../../assets/images/info-icon.png';
import trashIcon from '../../assets/images/trash-icon.png';
import { formatUSD } from '../../utils/common';

const PreQuoteSalesTable = ({ salesData, setFormData, products, productMap, multiplierMap, multiplierOptions, rowErrors }) => {

    const TAX_RATE = 0.00; // 0%

    // --- Helper Logic ---

    const calculateRowTotal = (unitPrice, quantity, multipliervalue) => {
        const qty = Number(quantity || 0);
        const price = Number(unitPrice || 0);
        const mult = multipliervalue ? Number(multipliervalue || 1) : 1;
        return (price * qty * mult).toFixed(2);
    };

    const getFinalTotals = (rows) => {
        const subTotal = rows.reduce((sum, row) => sum + Number(row.total_price || 0), 0);
        const salesTax = subTotal * TAX_RATE;
        const total = subTotal + salesTax;

        return {
            sub_total: subTotal.toFixed(2),
            sales_tax: salesTax.toFixed(2),
            total: total.toFixed(2)
        };
    };

    // --- Event Handlers ---

    const handleProductSelect = (index, selected) => {
        if (!selected) return;
        const product = productMap[selected.value];
        if (!product) return;

        setFormData(prev => {
            const rows = [...prev.sales_data];
            
            // We store the RAW price from the product map as the unitPrice
            const basePrice = product.price || 0;
            const currentQty = rows[index].quantity || 0;
            const currentMult = rows[index].multiplier;

            rows[index] = {
                ...rows[index],
                product_number: product.num,
                description: product.description,
                unit_price: basePrice, 
                total_price: calculateRowTotal(basePrice, currentQty, currentMult)
            };

            return {
                ...prev,
                sales_data: rows,
                ...getFinalTotals(rows)
            };
        });
    };

    const handleMultiplierSelect = (index, selected) => {
        const multValue = selected ? selected.value : null;
        
        setFormData(prev => {
            const rows = [...prev.sales_data];
            const currentRow = rows[index];

            rows[index] = {
                ...currentRow,
                multiplier: multValue,
                total_price: calculateRowTotal(currentRow.unit_price, currentRow.quantity, multValue)
            };

            return {
                ...prev,
                sales_data: rows,
                ...getFinalTotals(rows)
            };
        });
    };

    const handleQtyChange = (index, value) => {
        const qty = value === "" ? "" : Number(value);

        setFormData(prev => {
            let rows = [...prev.sales_data];
            const currentRow = rows[index];

            rows[index] = {
                ...currentRow,
                quantity: qty,
                total_price: calculateRowTotal(currentRow.unit_price, qty, currentRow.multiplier)
            };

            // Auto-add row logic: if user types in the last row, add a new empty one
            if (qty > 0 && index === rows.length - 1) {
                rows.push({
                    type: "Sale",
                    product_number: "",
                    description: "",
                    unit_price: "0.00",
                    quantity: "",
                    total_price: "0.00",
                    multiplier: null
                });
            }

            return {
                ...prev,
                sales_data: rows,
                ...getFinalTotals(rows)
            };
        });
    };

    const removeRow = (index) => {
        setFormData(prev => {
            const rows = prev.sales_data.filter((_, i) => i !== index);
            return {
                ...prev,
                sales_data: rows,
                ...getFinalTotals(rows)
            };
        });
    };

    // --- Render ---

    return (
        <table className="table table-bordered techtop-quote-table2">
            <thead>
                <tr>
                    <th className="text-center" style={{ width: "6%" }}>Item #</th>
                    <th className="text-center">Type</th>
                    <th className="text-center" style={{ width: "25%" }}>Number</th>
                    <th className="text-center" style={{ width: "30%" }}>Description</th>
                    <th className="text-center">Unit Price</th>
                    <th className="text-center" style={{ width: "8%" }}>Qty Ordered</th>
                    <th className="text-center" style={{ width: "17%" }}>
                        <a href="https://techtopind.com/static/commission-scales-2025.09.pdf" target="_blank" rel="noopener noreferrer">
                            <img src={infoIcon} alt="Info" style={{ width: "20px" }} />
                        </a>
                    </th>
                    <th className="text-center">Total Price</th>
                    <th className="text-center">Action</th>
                </tr>
            </thead>
            <tbody>
                {salesData.map((row, index) => (
                    <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td className="text-center">{row.type}</td>
                        <td>
                            <Select
                                options={products}
                                value={row.product_number ? { value: row.product_number, label: row.product_number } : null}
                                onChange={(opt) => handleProductSelect(index, opt)}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                            {rowErrors?.[index]?.product_number && (
                                <div className="text-danger small">{rowErrors[index].product_number}</div>
                            )}
                        </td>
                        <td>{row.description}</td>
                        <td className="text-center">${formatUSD(row.unit_price)}</td>
                        <td>
                            <input
                                type="number"
                                className="form-control"
                                value={row.quantity} 
                                min="1"
                                onChange={(e) => handleQtyChange(index, e.target.value)}
                            />
                            {rowErrors?.[index]?.quantity && (
                                <div className="text-danger small">{rowErrors[index].quantity}</div>
                            )}
                        </td>
                        <td>
                            <Select
                                options={multiplierOptions}
                                value={row.multiplier ? { value: row.multiplier, label: row.multiplier } : null}
                                onChange={(opt) => handleMultiplierSelect(index, opt)}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                            {rowErrors?.[index]?.multiplier && (
                                <div className="text-danger small">{rowErrors[index].multiplier}</div>
                            )}
                        </td>
                        <td className="text-center">${formatUSD(row.total_price)}</td>
                        <td className="text-center">
                            {salesData.length > 1 && (
                                <img 
                                    className="remove-icon" 
                                    src={trashIcon} 
                                    alt="Remove" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => removeRow(index)} 
                                />
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default React.memo(PreQuoteSalesTable);